/**
 * Tenant palette seam — per-tenant brand colours as runtime CSS variable
 * overrides.
 *
 * ────────────────────────────────────────────────────────────────────────
 * NOT CALLED ANYWHERE YET. This is the documented injection point so that
 * white-labelling can be added later without re-opening the token layer.
 * There is deliberately no API client, no fetch, and no assumed backend
 * response shape here.
 * ────────────────────────────────────────────────────────────────────────
 *
 * HOW IT WORKS
 * Roles are declared in stylesheets (`:root` for light, `[data-theme='dark']`
 * for dark). This writes to the INLINE style of <html>, which beats both
 * because inline styles win the cascade. So a tenant override applies in
 * light and dark alike, and the theme toggle keeps working underneath it.
 *
 * INTENDED USAGE
 *   // after the tenant's branding is known (login, tenant bootstrap, …):
 *   applyTenantPalette({'--brand-500': '#7B2D8E', '--brand-700': '#5A1F6B'})
 *   // on logout / tenant switch:
 *   clearTenantPalette()
 *
 * If a tenant needs different values per theme, call it again from wherever
 * the resolved theme changes, passing that theme's values — the overrides are
 * a flat layer on top and are replaced wholesale on each call.
 *
 * WHY AN ALLOWLIST
 * These values are expected to arrive from a server one day. A CSS custom
 * property value is injected verbatim into the cascade, so an unconstrained
 * `setProperty` is an injection sink (`red; background-image: url(...)`).
 * Both the property NAME and the VALUE are validated; anything rejected is
 * dropped and reported once via console.warn rather than throwing, so bad
 * branding data can never take the app down.
 */

/**
 * Tokens a tenant is allowed to override.
 *
 * Only brand/identity tokens — deliberately NOT text, surface or status
 * roles. Those carry the design system's WCAG guarantees, and letting a
 * tenant set `--text-primary` is how you ship an unreadable app.
 */
export const TENANT_OVERRIDABLE_TOKENS = [
  // Identity cyan scale (logo, banners, rings, dots)
  '--brand-50',
  '--brand-100',
  '--brand-200',
  '--brand-300',
  '--brand-400',
  '--brand-500',
  // Interaction anchors (CTA fill, links, active states, focus)
  '--brand-600',
  '--brand-700',
  '--brand-800',
  '--brand-900',
  // Text that sits on top of a brand fill — a tenant picking a light brand
  // colour must be able to flip this to dark ink to keep contrast.
  '--text-on-brand',
  // Data-visualisation accents
  '--viz-cyan',
  '--viz-cyan-soft',
] as const

export type TenantPaletteToken = (typeof TENANT_OVERRIDABLE_TOKENS)[number]

/** A partial palette — every token is optional. */
export type TenantPalette = Partial<Record<TenantPaletteToken, string>>

const ALLOWED = new Set<string>(TENANT_OVERRIDABLE_TOKENS)

/**
 * Accepts the colour notations a branding system realistically produces and
 * nothing else: #rgb/#rgba/#rrggbb/#rrggbbaa, rgb()/rgba()/hsl()/hsla() with
 * plain numeric args, and bare CSS colour keywords. No url(), no var(), no
 * semicolons, no comment sequences, no expressions.
 */
const SAFE_COLOR = /^(#[0-9a-f]{3,8}|(rgb|hsl)a?\(\s*[0-9a-z.%,/\s+-]{3,64}\)|[a-z]{3,24})$/i

export function isTenantPaletteToken(token: string): token is TenantPaletteToken {
  return ALLOWED.has(token)
}

function isSafeColorValue(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed || trimmed.length > 64) return false
  if (/[;{}<>\\]/.test(trimmed)) return false
  return SAFE_COLOR.test(trimmed)
}

function getRoot(root?: HTMLElement | null): HTMLElement | null {
  if (root) return root
  return typeof document === 'undefined' ? null : document.documentElement
}

/**
 * Apply a tenant palette as inline CSS variable overrides on <html>.
 *
 * Replaces any previously applied tenant palette (it does not merge), so the
 * element is always in exactly one known branding state.
 *
 * @returns the tokens that were actually applied, in declaration order.
 */
export function applyTenantPalette(
  palette: TenantPalette,
  root?: HTMLElement | null,
): TenantPaletteToken[] {
  const el = getRoot(root)
  if (!el) return []

  clearTenantPalette(el)

  const applied: TenantPaletteToken[] = []
  const rejected: string[] = []

  for (const [token, value] of Object.entries(palette)) {
    if (!isTenantPaletteToken(token) || !isSafeColorValue(value)) {
      rejected.push(token)
      continue
    }
    el.style.setProperty(token, value.trim())
    applied.push(token)
  }

  if (applied.length > 0) el.dataset.tenantPalette = applied.join(',')
  if (rejected.length > 0) {
    console.warn(
      `[tenant-palette] ignored ${rejected.length} invalid override(s): ${rejected.join(', ')}`,
    )
  }

  return applied
}

/**
 * Remove every tenant override previously applied by {@link applyTenantPalette},
 * returning the document to the design system's own values. Only the tokens
 * this module set are removed — unrelated inline styles on <html> (e.g. the
 * `color-scheme` written by the theme switcher) are left alone.
 */
export function clearTenantPalette(root?: HTMLElement | null): void {
  const el = getRoot(root)
  if (!el) return

  const applied = el.dataset.tenantPalette
  if (!applied) return

  applied
    .split(',')
    .filter(Boolean)
    .forEach((token) => el.style.removeProperty(token))

  delete el.dataset.tenantPalette
}
